import * as signalR from "@microsoft/signalr";
import { AuthenticatedUser } from "../entities/authenticated_user";
import {
  GanttOverviewResponse,
  GetGantOverviewFilters
} from "../entities/gantt";
import { OfficeAddPayload, OfficeUpdatePayload } from "../entities/office";
import { ReservationSettingsRequest } from "../entities/office_settings_request";
import {
  CreateOfficeDocumentPayload,
  OfficeDocument,
  OfficeDocumentUploadURLResponse
} from "../entities/office-document";
import { OfficesAvailabilityResponse } from "../entities/offices_availability";
import { Organization } from "../entities/organization";
import {
  GeneralSettingsRequest,
  QuestionnaireSettingsRequest
} from "../entities/organization_settings_request";
import { PaginationOptions } from "../entities/pagination_options";
import { QuestionnaireRequest } from "../entities/questionnaire";
import {
  IResource,
  MapCompositeType,
  MapIcon,
  ResourceMapVersion
} from "../entities/resource";
import type {
  DedicatedExceptionRequest,
  DedicatedExceptionResponse,
  IDedicatedException
} from "../entities/dedicated_exception";
import { ResourceAdditionalAccessOptions } from "../entities/resource_additional_access_options";
import { IResourcePermission } from "../entities/resource_permission";
import {
  CreateResourceRequest,
  EditResourceRequest
} from "../entities/resource_requests";
import {
  AvailabilityErrorCode,
  AvailabilityErrorDetails
} from "../entities/schedule";
import { ITeam, ITeamData } from "../entities/team";
import { IUserFull, UserRoles } from "../entities/user";
import { IUserResourceManaged } from "../entities/user_resource";
import {
  TeamEditRequestBody,
  UserEditRequestBody
} from "../entities/user_team_request";
import {
  CoreOffices,
  CoreResGetOffices,
  GetResourceManagerPermission,
  GetResourceSchedulePermission,
  IMediaUploadResponse,
  IMediaUploadResult,
  IMediaUri,
  IOrganizationsUsers,
  IOrganizationsUsersWithOptions,
  PostResourceManagerPermissions,
  PostResourceScheduledPermissions,
  PutResourceScheduledPermissions,
  ResGetFormattedOffices,
  ResGetMe,
  ResGetNews,
  ResGetOffice,
  ResGetOffices,
  ResGetOfficesQuestionnaire,
  ResGetOfficesResources,
  ResGetOrganization,
  ResGetOrganizations,
  ResGetQuestion,
  ResGetQuestions,
  ResGetResource,
  ResGetTeams,
  ResGetTeamsWithOptions,
  ResGetUser,
  ResGetUserId,
  ResGetUsers,
  ResGetUsersTeam,
  ResourcePermissionFilter
} from "../types/api-types";
import { formatOptions } from "../utils";
import { BaseOrganizationEndpoints, getHeaderSlug } from "./base";
import { PaginatedResult } from "./response/paginated_result";
import { ResponseWithStatus } from "./response/response_with_status";

export interface UploadImageOptions {
  type: "map:background" | "map:furniture";
  format: "svg" | "png" | "jpg";
}

export interface GetAllResourcesFilters {
  officeId: string;
  types: string[];
  parentId?: string | null;
  search?: string | null;
  /**
   * When provided, the resource's `user_visible_assignments` and
   * `team_visible_assignments` are returned for this date (format: yyyy-MM-dd).
   */
  assignments_date?: string | null;
}

export interface GetResourcesFilters {
  officeId: string;
  types: string[];
  parentId?: string | null;
  date?: string[] | string | null;
  startDate?: string | null;
  endDate?: string | null;
  duration?: string | null;
  query?: string | null;
  search?: string | null;
  limit?: number;
  hierarchy?: boolean;
  manage?: boolean;
  organizerId?: string | null;
  tags?: string[] | null;
  tags_operator?: "OR" | "AND";
  assignments_date?: string | null;
}

// The `/v1/users` endpoint rejects page sizes above 500, so callers that omit
// an explicit limit must fall back to this rather than the generic
// `MAXIMUM_LIMIT` (2000) used by other paginated endpoints.
const USERS_MAXIMUM_LIMIT = 500;

export class Organizations extends BaseOrganizationEndpoints {
  async get(slug: string): Promise<ResponseWithStatus<Organization>> {
    return this.requestWithStatus<Organization>(
      "GET",
      `/organizations/${encodeURIComponent(slug)}`
    );
  }

  async getMobile(slug: string): Promise<ResponseWithStatus<Organization>> {
    return this.requestWithStatus<Organization>(
      "GET",
      `/api/organizations/${encodeURIComponent(slug)}`
    );
  }

  async getById(
    authorization: string,
    id: string
  ): Promise<ResponseWithStatus<Organization>> {
    return this.requestWithStatus<Organization>(
      "GET",
      `/id/organizations/${id}`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  /**
   * Primary endpoint for returning the current authenticated user, within the context of the current org
   */
  async getOrgMe() {
    return this.requestWithStatus<ResGetUser>(
      "GET",
      `${this.settings.adminAuth ? "/integrations" : ""}/user`
    );
  }

  isDeskBookingEnabled(organization: Organization): boolean {
    return organization.desk_bookings_enabled;
  }

  isParkingBookingEnabled(organization: Organization): boolean {
    return organization.parking_bookings_enabled;
  }

  isMeetingRoomBookingEnabled(organization: Organization): boolean {
    return organization.meeting_room_bookings_enabled;
  }

  /**
   * Updates the organization's logo with the provided image URL(s).
   * Accepts logo_url and/or squared_logo_url — only provided fields are updated.
   */
  async updateIcon(body: { logo_url?: string; squared_logo_url?: string }) {
    return await this.requestWithStatus("PUT", "/organizations/logo", body);
  }

  async updateQuestionnaireSettings(body: QuestionnaireSettingsRequest) {
    return await this.requestWithStatus<Organization>(
      "PUT",
      "/organizations/questionnaire/settings",
      body
    );
  }

  async updateGeneralSettings(body: GeneralSettingsRequest) {
    return await this.requestWithStatus<Organization>(
      "PUT",
      "/organizations/general/settings",
      body
    );
  }

  async join() {
    const response = await this.requestWithStatus<{
      enabled: boolean;
      join: string;
    }>("GET", "/invitations/join");
    return {
      ...response,
      result: {
        enabled: response.result ? response.result.enabled : false,
        url:
          response.result && response.result.enabled
            ? this.getJoinUrl(response.result.join)
            : null
      }
    };
  }

  async joinEnable(body: { enable: boolean; generate: boolean }) {
    const response = await this.requestWithStatus<{
      enabled: boolean;
      join: string;
    }>("PUT", "/invitations/join", body);
    return {
      ...response,
      result: {
        enabled: response.result ? response.result.enabled : false,
        url:
          response.result && response.result.enabled
            ? this.getJoinUrl(response.result.join)
            : null
      }
    };
  }

  /**
   * Creates the authenticatedUser object for the currently
   * authenticated user.
   */
  me(
    user: ResGetUser,
    organization: ResGetOrganization,
    organizations: ResGetOrganizations,
    offices: ResGetFormattedOffices | null
  ): AuthenticatedUser {
    const slug = this.getSlug();

    const authenticatedUser: AuthenticatedUser = {
      user: user,
      slug: slug,
      redirect: {
        setup: false,
        login: false,
        subdomain: null,
        url: null
      },
      permissions: {
        all: [],
        impersonate: false,
        schedule_others: false,
        schedule_others_managed: false,
        schedule_respond: false,
        settings: false,
        enterprise: false
      },
      organizations: organizations ? organizations.elements : [],
      organization: organization,
      offices: offices ? offices : null,
      officeId: offices && offices.officeId ? offices.officeId : null,
      defaultAreaId: user?.organization_user?.default_area_id ?? null
    };

    // Guard against an office ID being saved for a user that's not active
    const defaultOfficeNotDeleted = offices
      ? offices.offices.some(
          (office) => office.id === user.organization_user?.default_office_id
        )
      : false;
    const validDefaultOfficeId =
      offices && defaultOfficeNotDeleted
        ? user.organization_user?.default_office_id
        : offices?.offices[0].id;
    if (authenticatedUser.offices) {
      authenticatedUser.offices.officeId = validDefaultOfficeId ?? null;
    }

    const permissions = user.organization_user
      ? user.organization_user?.permissions
      : [];
    authenticatedUser.permissions = {
      all: permissions,
      impersonate: permissions.includes("admin:impersonate"),
      schedule_others: permissions.includes("schedule:others:add"),
      schedule_others_managed: permissions.includes(
        "schedule:others:managed:add"
      ),
      schedule_respond: permissions.includes("schedule:respond"),
      settings: permissions.includes("office:add"),
      enterprise: permissions.includes("organization:enterprise")
    };

    return authenticatedUser;
  }

  async updateMe(body: object) {
    return await this.requestWithStatus("PUT", "/user", body);
  }

  async getUserByEmail(
    authorization: string,
    organizationId: string,
    email: string
  ) {
    return await this.requestWithStatus<IUserFull>(
      "GET",
      `/organizations/${organizationId}/users/${email}`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  async updateUserPhoto(
    authorization: string,
    organizationId: string,
    userId: string,
    url: string
  ) {
    return await this.requestWithStatus<IUserFull>(
      "PUT",
      `/organizations/${organizationId}/users/${userId}/photo`,
      {
        profile_photo_url: url
      },
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  /**
   * Updates the user's profile image with the provided image
   */
  async updatePhoto(body: {
    profile_photo_url: string | null;
  }): Promise<ResponseWithStatus<IUserFull>> {
    return await this.requestWithStatus("PUT", "/user/logo", body);
  }

  /**
   * Update a users's default office, within the context of the organization
   */
  async updateDefaultOffice(body: {
    office_id: string;
  }): Promise<ResponseWithStatus<IUserFull>> {
    return await this.requestWithStatus("PUT", "/user/offices/default", body);
  }

  async resetPassword(body: object) {
    return await this.requestWithStatus("DELETE", "/user/password", body);
  }

  /**
   * Update the organization's settings
   */
  async update(body: object) {
    return await this.requestWithStatus("PUT", "/organizations", body);
  }

  async usersPaginated(filters: URLSearchParams) {
    return this.requestWithStatus<ResGetUsers>(
      "GET",
      `/v1/users?${filters.toString()}`
    );
  }

  async usersWithOptions(
    query: string | null,
    roles: UserRoles[] | null,
    pagination: PaginationOptions = {
      cursor: null,
      limit: null
    },
    managed = false
  ): Promise<ResponseWithStatus<IOrganizationsUsersWithOptions>> {
    const res = await this.users(query, roles, pagination, managed);
    if (res.ok && res.result) {
      const formatted = formatOptions<IOrganizationsUsers, IUserFull>(
        res.result
      );
      return { ...res, result: formatted };
    }
    return res as unknown as ResponseWithStatus<IOrganizationsUsersWithOptions>;
  }

  async organizationUsers(
    authorization: string,
    organizationId: string,
    roles: UserRoles[] | null,
    pagination: PaginationOptions | null
  ): Promise<ResponseWithStatus<ResGetUsers>> {
    const params = new URLSearchParams();
    if (roles) {
      roles.forEach((role) => params.append("role", role));
    }
    const parmsString = params.toString();

    return this.requestWithStatus<ResGetUsers>(
      "GET",
      this.paginatedEndpointUrl(
        `/organizations/${organizationId}/users`,
        pagination
      ) + (parmsString ? `&${parmsString}` : ""),
      null,
      { authHeader: `Basic ${authorization}` }
    );
  }

  async users(
    query: string | null,
    roles: UserRoles[] | null,
    pagination: PaginationOptions = {
      cursor: null,
      limit: null
    },
    managed = false
  ): Promise<ResponseWithStatus<IOrganizationsUsers>> {
    const result: IOrganizationsUsers = {
      letters: {},
      cursor: null,
      elements: []
    };

    const params = new URLSearchParams();
    if (query) {
      params.set("search", query);
    }
    if (roles) {
      roles.forEach((role) => params.append("role", role));
    }
    const parmsString = params.toString();

    // `/v1/users` caps page size at 500; clamp here so the null-limit default
    // (which would otherwise resolve to MAXIMUM_LIMIT = 2000 in
    // paginatedEndpointUrl) stays within what the API accepts.
    const requestedLimit =
      pagination.limit != null ? Number(pagination.limit) : USERS_MAXIMUM_LIMIT;
    const usersPagination: PaginationOptions = {
      cursor: pagination.cursor,
      limit: String(
        Math.min(
          Number.isFinite(requestedLimit)
            ? requestedLimit
            : USERS_MAXIMUM_LIMIT,
          USERS_MAXIMUM_LIMIT
        )
      )
    };

    const baseUrl = managed ? `/users/managed` : `/v1/users`;
    const response = await this.requestWithStatus<ResGetUsers>(
      "GET",
      this.paginatedEndpointUrl(baseUrl, usersPagination) +
        (parmsString ? `&${parmsString}` : "")
    );

    if (!response.ok) {
      return response as ResponseWithStatus<IOrganizationsUsers>;
    }
    result.cursor = response.result?.cursor || null;
    result.elements = response.result?.elements || [];
    for (const element of result.elements) {
      const name = (element.name || element.email || "").trim();
      if (name.length === 0) {
        continue;
      }

      const letter = name.slice(name.lastIndexOf(" ") + 1)[0].toUpperCase();
      if (typeof result.letters[letter] === "undefined") {
        result.letters[letter] = [];
      }
      result.letters[letter].push(element);
    }

    return { ...response, result };
  }

  /**
   * @deprecated use teams.forUser()
   */
  async userTeamList(userId: string, showHidden = false) {
    return this.requestWithStatus<ResGetUsersTeam>(
      "GET",
      `/users/${encodeURIComponent(userId)}/teams${showHidden ? "?hidden" : ""}`
    );
  }

  async usersById(ids: string[]) {
    const all = ids.map((id) =>
      this.requestWithStatus<ResGetUserId>(
        "GET",
        `/v1/users/${encodeURIComponent(id)}`
      )
    );
    const results = await Promise.all(all);
    return results.filter((x) => x.ok).map((x) => x.result);
  }

  /** The calling user with the zones they manage (`GET /v1/users/me`). */
  async userMe() {
    return await this.requestWithStatus<ResGetMe>("GET", "/v1/users/me");
  }

  async user(id: string, showHidden = false) {
    const promises = await Promise.all([
      this.requestWithStatus<ResGetUserId>(
        "GET",
        `/v1/users/${encodeURIComponent(id)}`
      ),
      this.requestWithStatus<ResGetUsers>(
        "GET",
        `/users/${encodeURIComponent(id)}/colleagues`
      ),
      this.userTeamList(id, showHidden)
    ] as const);

    promises.forEach((prom, i) => {
      if (!prom.ok) {
        throw new Error(
          `Organizations.user(${id}): Impossible to complete request #${i} - ${JSON.stringify(prom.result)}`
        );
      }
    });
    const [user, colleagues, teams] = promises.map((x) => x.result);

    return {
      user: user as IUserFull,
      colleagues: colleagues as ResGetUsers,
      teams: teams as ITeamData
    } as const;
  }

  userColleagues(id: string) {
    return this.requestWithStatus<ResGetUsers>(
      "GET",
      `/users/${encodeURIComponent(id)}/colleagues`
    );
  }

  async userUpdate(id: IUserFull["id"], body: UserEditRequestBody) {
    return await this.requestWithStatus<ResGetUserId>(
      "PUT",
      `/users/${encodeURIComponent(id)}`,
      body
    );
  }

  async userDelete(id: string) {
    return await this.requestWithStatus<ResGetUserId>(
      "DELETE",
      `/users/${encodeURIComponent(id)}`
    );
  }

  /**
   * Gets all of an organizations offices
   */
  async offices(): Promise<ResponseWithStatus<ResGetFormattedOffices>> {
    const noAdminEndpoint = "/v1/offices";
    const adminEndpoint = "/integrations/offices";

    const response = await this.requestWithStatus<CoreResGetOffices>(
      "GET",
      `${this.settings.adminAuth ? adminEndpoint : noAdminEndpoint}?limit=` +
        BaseOrganizationEndpoints.MAXIMUM_LIMIT
    );
    if (!response.ok) {
      return response as unknown as ResponseWithStatus<ResGetFormattedOffices>;
    }
    return {
      ...response,
      result:
        response &&
        response.result &&
        response.result.elements &&
        response.result.elements.length > 0
          ? await this.formatOffices(response.result)
          : null
    };
  }

  /**
   * Returns the details of a single office
   */
  async office(id: string) {
    const office = await this.requestWithStatus<ResGetOffice>(
      "GET",
      `${this.settings.adminAuth ? "/integrations" : ""}/offices/${encodeURIComponent(id)}`
    );
    return office;
  }

  async officeAdd(body: OfficeAddPayload) {
    return await this.requestWithStatus("POST", "/offices", body);
  }

  async officeUpdate(id: string, body: OfficeUpdatePayload) {
    return await this.requestWithStatus(
      "PUT",
      `/offices/${encodeURIComponent(id)}`,
      body
    );
  }

  async officeDelete(id: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/v1/offices/${encodeURIComponent(id)}`
    );
  }

  async officeDocumentsList(
    officeId: string,
    options?: { cursor?: string; limit?: number }
  ) {
    const params = new URLSearchParams();
    if (options?.cursor) params.set("cursor", options.cursor);
    if (options?.limit) params.set("limit", String(options.limit));
    const query = params.toString();
    return await this.requestWithStatus<PaginatedResult<OfficeDocument>>(
      "GET",
      `/v1/offices/${encodeURIComponent(officeId)}/documents${query ? `?${query}` : ""}`
    );
  }

  async officeDocumentsUploadUrl() {
    return await this.requestWithStatus<OfficeDocumentUploadURLResponse>(
      "POST",
      "/v1/offices/documents/upload"
    );
  }

  async officeDocumentsCreate(
    officeId: string,
    body: CreateOfficeDocumentPayload
  ) {
    return await this.requestWithStatus<OfficeDocument>(
      "POST",
      `/v1/offices/${encodeURIComponent(officeId)}/documents`,
      body
    );
  }

  async officeDocumentsDelete(officeId: string, documentId: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/v1/offices/${encodeURIComponent(officeId)}/documents/${encodeURIComponent(documentId)}`
    );
  }

  async updateOfficeReservationSettings(
    id: string,
    body: ReservationSettingsRequest
  ) {
    return await this.requestWithStatus<ResGetOffice>(
      "PUT",
      `/offices/${encodeURIComponent(id)}/reservation/settings`,
      body
    );
  }

  /**
   * @deprecated use teams.all()
   */
  async teams(showHidden = false) {
    return await this.requestWithStatus<ResGetTeams>(
      "GET",
      `/teams${showHidden ? "?hidden" : ""}`
    );
  }

  /**
   * @deprecated use teams.all()
   */
  async teamsWithOptions(
    showHidden = false
  ): Promise<ResponseWithStatus<ResGetTeamsWithOptions>> {
    const res = await this.teams(showHidden);
    if (res.ok && res.result) {
      return {
        ...res,
        result: formatOptions(res.result) as unknown as ResGetTeamsWithOptions
      };
    }
    return res as unknown as ResponseWithStatus<ResGetTeamsWithOptions>;
  }

  /*
   * Returns all users asigned to a specific team
   *
   * @param id The id of the team
   * @param query text search on ????? TODO: Figure out what TEXT_SEARCH_CONFIGURATION applies
   * @returns
   *
   * @deprecated
   */
  async teamUsers(
    id: string,
    query: string | null = null
  ): Promise<ResponseWithStatus<ResGetUsers>> {
    return await this.requestWithStatus(
      "GET",
      `/teams/${encodeURIComponent(id)}/users${query ? `?query=${encodeURIComponent(query)}` : ""}`
    );
  }

  /**
   * @deprecated use teams.add()
   */
  async teamAdd(body: object) {
    return await this.requestWithStatus("POST", "/teams", body);
  }

  /**
   * @deprecated use teams.update()
   */
  async teamUpdate(id: ITeam["id"], body: TeamEditRequestBody) {
    return await this.requestWithStatus(
      "PUT",
      `/teams/${encodeURIComponent(id)}`,
      body
    );
  }

  /**
   * @deprecated use teams.delete()
   */
  async teamDelete(id: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/teams/${encodeURIComponent(id)}`
    );
  }

  /**
   * @deprecated use teams.addMember()
   */
  async teamAddUser(teamId: string, userId: string) {
    return await this.requestWithStatus<ITeam>(
      "POST",
      `/teams/${encodeURIComponent(teamId)}/users/${userId}`
    );
  }
  /**
   * @deprecated use teams.removeMember()
   */
  async teamDeleteUser(teamId: string, userId: string) {
    return await this.requestWithStatus<ITeam>(
      "DELETE",
      `/teams/${encodeURIComponent(teamId)}/users/${userId}`
    );
  }

  async getSchedulableResources(filters: GetResourcesFilters) {
    const params = this.getGetResourcesFilterParams(filters, true);

    const resources = await this.requestWithStatus<ResGetOfficesResources>(
      "GET",
      `/v1/resources/schedulable?${params.toString()}`
    );

    if (!resources.ok || !resources.result) {
      return resources as unknown as ResponseWithStatus<{
        available: IResource[];
        nonAvailable?: {
          resource: IResource;
          reason_code: AvailabilityErrorCode;
          details?: AvailabilityErrorDetails;
          reason: string;
        }[];
      }>;
    }

    return {
      ...resources,
      result: {
        available: resources.result.elements,
        nonAvailable: resources.result.non_available || []
      }
    };
  }

  /**
   * Get the query parameters for the resources endpoint
   * @param filters
   * @param isCoreApi is true for /v1/* endpoints
   * @returns
   */
  private getGetResourcesFilterParams(
    filters: GetResourcesFilters,
    isCoreApi = false
  ) {
    const params = new URLSearchParams();
    if (!isCoreApi && filters.hierarchy) {
      // Legacy (/offices/*) endpoints only — the core /v1/* endpoints ignore
      // this arg entirely. The query arg must be ommitted rather than set to
      // false: ?hierarchy=false still returns hierarchical results.
      params.append("hierarchy", "1");
    }
    if (filters.manage) {
      params.append("manage", "1");
    }

    const orgKey = isCoreApi ? "as_user_id" : "organizer";
    if (filters.organizerId) {
      params.append(orgKey, filters.organizerId);
    }
    for (const type of filters.types) {
      params.append("type", type);
    }
    if (Array.isArray(filters.tags) && filters.tags.length) {
      for (const tag of filters.tags) {
        params.append("tags", tag);
      }
    }

    if (filters.tags_operator) {
      params.append("tags_operator", filters.tags_operator);
    }

    if (filters.parentId) {
      params.append("parent", filters.parentId);
    }

    if (filters.officeId) {
      params.append("office_id", filters.officeId);
    }
    if (filters.startDate && filters.endDate) {
      params.append("start_time", filters.startDate);
      params.append("end_time", filters.endDate);
    }
    const dates =
      typeof filters.date === "string" ? [filters.date] : filters.date;
    if (dates) {
      // Validate duration is a valid positive integer before appending
      // This prevents sending invalid values like "NaN", "Infinity", or "120.5" to the API
      const durationNum = filters.duration
        ? parseInt(filters.duration.toString(), 10)
        : NaN;
      const isValidDuration =
        !isNaN(durationNum) && durationNum > 0 && isFinite(durationNum);

      dates.forEach((x) => {
        params.append("date", x);
        if (isValidDuration) {
          params.append("duration", durationNum.toString());
        }
      });
    }
    if (filters.query) {
      params.append("query", filters.query);
    }
    if (filters.search) {
      params.append("search", filters.search);
    }
    if (filters.assignments_date) {
      params.append("assignments_date", filters.assignments_date);
    }
    if (filters.limit) params.set("limit", filters.limit.toString());

    return params;
  }

  async getAllResources(filters: GetAllResourcesFilters) {
    const {
      officeId,
      types,
      parentId = null,
      search = null,
      assignments_date = null
    } = filters;

    if (types.length === 0) {
      throw new Error("At least one type is required");
    }
    const params = this.getGetResourcesFilterParams(
      {
        officeId,
        types,
        parentId,
        search,
        assignments_date
      },
      true
    );

    const resources = await this.requestWithStatus<ResGetOfficesResources>(
      "GET",
      `/v1/resources?${params.toString()}`
    );

    if (!resources.ok || !resources.result) {
      return resources;
    }

    const byId: { [key: string]: IResource } = {};
    const options = [];
    for (const element of resources.result?.elements || []) {
      byId[element.id] = element;
      options.push({ value: element.id, label: element.name });
    }
    const result = {
      ...resources.result,
      byId: byId,
      options: options
    };

    return { ...resources, result };
  }

  async resourcesAll(
    officeId: string,
    types: string[],
    parentId: string | null = null,
    filter: string | null = null,
    extended = true,
    date: string[] | string | null = null,
    query: string | null = null,
    duration: string | null = null,
    limit: number = BaseOrganizationEndpoints.MAXIMUM_LIMIT,
    hierarchy = false,
    manage = false,
    organizerId: string | null = null
  ) {
    if (types.length === 0) {
      throw new Error("At least one type is required");
    }
    const params = this.getGetResourcesFilterParams(
      {
        officeId,
        types,
        parentId,
        date,
        query,
        duration,
        limit,
        hierarchy,
        manage,
        organizerId
      },
      false
    );

    const resources = await this.requestWithStatus<ResGetOfficesResources>(
      "GET",
      `${this.settings.adminAuth ? "/integrations" : ""}/offices/${encodeURIComponent(officeId)}/resources${
        filter ? `/${encodeURIComponent(filter)}` : ""
      }?${params.toString()}`
    );
    if (!extended) {
      return resources;
    }

    if (!resources.ok || !resources.result) {
      return resources;
    }

    const byId: { [key: string]: IResource } = {};
    const options = [];
    for (const element of resources.result?.elements || []) {
      byId[element.id] = element;
      options.push({ value: element.id, label: element.name });
    }
    const result = {
      ...resources.result,
      byId: byId,
      options: options
    };

    return { ...resources, result };
  }

  /** Users a resource is dedicated to (nobody else can book it). */
  async resourceDedicatedUsersGet(resourceId: string) {
    return this.requestWithStatus<PaginatedResult<IUserFull>>(
      "GET",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_users`
    );
  }

  /** Dedicate a resource to a user. `confirm_move: true` confirms moving
   *  a user who already has another dedicated resource. */
  async resourceDedicatedUserPost(
    resourceId: string,
    body: { user_id: string; confirm_move: boolean }
  ) {
    return this.requestWithStatus<IUserFull>(
      "POST",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_users`,
      body,
      // A 409 here is an expected, meaningful answer (the user already has
      // a dedicated resource; its body names it) — callers inspect the
      // status instead of catching a thrown ApiClientError.
      { notThrowWithInvalidResponses: true }
    );
  }

  async resourceDedicatedUserDelete(resourceId: string, userId: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_users/${encodeURIComponent(userId)}`,
      null,
      // Callers surface failures through response.ok (toast) — don't throw.
      { notThrowWithInvalidResponses: true }
    );
  }

  /** The days a dedicated desk is released while its dedicated user is away. */
  async resourceDedicatedExceptionsGet(resourceId: string) {
    return this.requestWithStatus<IDedicatedException[]>(
      "GET",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_exceptions`
    );
  }

  /**
   * Release a dedicated seat for some days. Allowed for the seat's own
   * dedicated user and for resource managers. `dedicated_user_id` names the
   * seat and defaults to the caller's own. Callers read the status:
   * - 409 `dedicated_exception_overlaps`: the days overlap another release of
   *   the same seat.
   * - 400 `dedicated_user_id_required`: a manager on a workspace with several
   *   dedicated users did not name one.
   * - 400 `not_a_dedicated_user`: the named user owns no seat here.
   */
  async resourceDedicatedExceptionPost(
    resourceId: string,
    body: DedicatedExceptionRequest
  ) {
    return this.requestWithStatus<DedicatedExceptionResponse>(
      "POST",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_exceptions`,
      body,
      { notThrowWithInvalidResponses: true }
    );
  }

  async resourceDedicatedExceptionPut(
    resourceId: string,
    exceptionId: string,
    body: DedicatedExceptionRequest
  ) {
    return this.requestWithStatus<DedicatedExceptionResponse>(
      "PUT",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_exceptions/${encodeURIComponent(exceptionId)}`,
      body,
      { notThrowWithInvalidResponses: true }
    );
  }

  /** Withdraw a release; the answer says how many reservations that cancelled. */
  async resourceDedicatedExceptionDelete(
    resourceId: string,
    exceptionId: string
  ) {
    return this.requestWithStatus<{ cancelled_schedules: number }>(
      "DELETE",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_exceptions/${encodeURIComponent(exceptionId)}`,
      null,
      { notThrowWithInvalidResponses: true }
    );
  }

  /**
   * The releases on the desks dedicated to a user, from `from` (`yyyy-MM-dd`,
   * default today) on. `me` stands for the signed-in user.
   */
  async userDedicatedExceptionsGet(userId: string, from?: string) {
    const params = new URLSearchParams();
    if (from) {
      params.set("from", from);
    }
    const query = params.toString();
    return this.requestWithStatus<IDedicatedException[]>(
      "GET",
      `/v1/users/${encodeURIComponent(userId)}/dedicated_exceptions${query ? `?${query}` : ""}`
    );
  }

  async resourceGet(id: string) {
    return await this.requestWithStatus<{ resource: ResGetResource }>(
      "GET",
      `/v1/resources/${encodeURIComponent(id)}`
    );
  }

  // Resolve an NFC/QR tag id (tagz_…) to the resource it is assigned to.
  async getAssignedResource(tagId: string) {
    return await this.requestWithStatus<{ resource: ResGetResource }>(
      "GET",
      `/tags/${encodeURIComponent(tagId)}`
    );
  }

  async resourceMapsGet(resourceId: string) {
    return this.requestWithStatus<PaginatedResult<ResourceMapVersion>>(
      "GET",
      `/v1/resources/${encodeURIComponent(resourceId)}/maps`
    );
  }

  async resourceMapPost(
    resourceId: string,
    body: { map_data: string; map_active_from?: string }
  ) {
    return this.requestWithStatus<ResourceMapVersion>(
      "POST",
      `/v1/resources/${encodeURIComponent(resourceId)}/maps`,
      body
    );
  }

  async mapIconsGet() {
    return this.requestWithStatus<PaginatedResult<MapIcon>>(
      "GET",
      "/v1/maps/icons"
    );
  }

  async mapIconPost(body: { name: string; svg: string; category: string }) {
    return this.requestWithStatus<MapIcon>("POST", "/v1/maps/icons", body);
  }

  async compositeTypesGet() {
    return this.requestWithStatus<PaginatedResult<MapCompositeType>>(
      "GET",
      "/v1/maps/composite-types"
    );
  }

  async compositeTypePost(body: { name: string; data: unknown }) {
    return this.requestWithStatus<MapCompositeType>(
      "POST",
      "/v1/maps/composite-types",
      body
    );
  }

  async compositeTypePut(id: string, body: { name: string }) {
    return this.requestWithStatus<MapCompositeType>(
      "PUT",
      `/v1/maps/composite-types/${encodeURIComponent(id)}`,
      body
    );
  }

  async compositeTypeDelete(id: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `/v1/maps/composite-types/${encodeURIComponent(id)}`
    );
  }

  async resourceMapActivate(resourceId: string, mapVersionId: string) {
    return this.requestWithStatus<ResourceMapVersion>(
      "PUT",
      `/v1/resources/${encodeURIComponent(resourceId)}/maps/${encodeURIComponent(mapVersionId)}`,
      { map_active_from: new Date().toISOString() }
    );
  }

  async resourcePut(id: string, request: EditResourceRequest) {
    const body = this.prepareRequest(request);
    return this.requestWithStatus<IResource>(
      "PUT",
      `/v1/resources/${encodeURIComponent(id)}`,
      body
    );
  }

  async resourcePost(officeId: string, request: EditResourceRequest) {
    const body = this.prepareRequest(request);
    return this.requestWithStatus<IResource>("POST", "/v1/resources", {
      ...body,
      office_id: officeId
    });
  }

  async batchResourcePost(resources: CreateResourceRequest[]) {
    return this.requestWithStatus<IResource[]>("POST", "/v1/resources/batch", {
      resources: resources.map((resource) => {
        const data = this.prepareRequest(resource) as unknown as IResource;
        data.office_id = resource.office_id;
        return data;
      })
    });
  }

  async batchResourcePut(resources: CreateResourceRequest[]) {
    return this.requestWithStatus<IResource[]>("PUT", "/v1/resources/batch", {
      resources: resources.map((resource) => {
        return { ...resource, office_id: resource.office_id };
      })
    });
  }

  async batchResourceDelete(ids: string[]) {
    return this.requestWithStatus<IResource[]>(
      "DELETE",
      "/v1/resources/batch",
      {
        resource_ids: ids.map((id) => {
          return id;
        })
      }
    );
  }

  async resourceMediaDelete(resourceId: string, mediaId: string) {
    return this.requestWithStatus(
      "DELETE",
      `/resources/${encodeURIComponent(resourceId)}/media/${mediaId}`
    );
  }

  async resourceMedia(resourceId: string) {
    return this.requestWithStatus<PaginatedResult<IMediaUploadResult>>(
      "GET",
      `/resources/${encodeURIComponent(resourceId)}/media`
    );
  }

  async resourceMediaUpload(
    resourceId: string,
    file: File,
    replaceAll = false,
    onProgress?: (progress: number) => void
  ) {
    const uploadUri = await this.resourceCreateMediaUri(resourceId);
    if (!uploadUri.ok || !uploadUri.result) {
      throw new Error("Could not get upload uri");
    }
    const upload = await this.resourceMediaUploadToProvider(
      uploadUri.result,
      file,
      onProgress
    );
    if (!upload.success) {
      throw new Error("Could not upload file");
    }
    const uri = await this.resourceMediaUri(resourceId, uploadUri.result.id);
    return this.resourceMediaAttachToResource(
      resourceId,
      uri.result?.uri ?? "",
      uploadUri.result.id,
      replaceAll
    );
  }

  async resourceMediaAttachToResource(
    resourceId: string,
    uri: string,
    uploadId: string,
    replaceAll = false
  ) {
    if (replaceAll) {
      const resource = await this.resourceGet(resourceId);
      if (!resource.ok || !resource.result) {
        throw new Error("Could not get resource");
      }

      return this.requestWithStatus<IMediaUploadResult>(
        "PUT",
        `/resources/${encodeURIComponent(resourceId)}`,
        {
          ...resource.result.resource,
          medias_patch: false,
          medias: [
            {
              upload_id: uploadId,
              type: "image",
              uri
            }
          ]
        }
      );
    }

    return this.requestWithStatus<IMediaUploadResult>(
      "POST",
      `/resources/${encodeURIComponent(resourceId)}/media`,
      {
        type: "image",
        uri,
        upload_id: uploadId
      }
    );
  }

  async resourceCreateMediaUri(resourceId: string) {
    return this.requestWithStatus<IMediaUri>(
      "POST",
      `/resources/${encodeURIComponent(resourceId)}/media/upload/image`
    );
  }

  async resourceMediaUri(resourceId: string, uploadId: string) {
    return this.requestWithStatus<IMediaUri>(
      "GET",
      `/resources/${encodeURIComponent(resourceId)}/media/upload/image/${encodeURIComponent(uploadId)}`
    );
  }

  async resourceMediaUploadToProvider(
    url: IMediaUri,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<IMediaUploadResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append("file", file);

      xhr.open("POST", url.uri, true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const json = JSON.parse(xhr.responseText);
            resolve(json);
          } catch (err) {
            reject(new Error("Invalid JSON response"));
          }
        } else {
          reject(
            new Error(
              `Upload failed with status ${xhr.status}: ${xhr.responseText}`
            )
          );
        }
      };

      xhr.onerror = () => {
        reject(new Error("Network error during upload"));
      };

      xhr.send(formData);
    });
  }

  private prepareRequest(request: EditResourceRequest) {
    const type = request.type;
    if (!type) {
      throw new Error("No resource type");
    }

    const isArea = type === "area";
    const isOpen = typeof request.is_open !== "undefined" && request.is_open;

    const totalSlots = request.total_slots ?? 1;
    const maximumSlotsPerUser = request.maximum_slots_per_user ?? 1;
    return {
      type: type,
      name: request.name,
      description: request.description,
      map_url: request.map_url,
      label: request.label,
      parent_id: request.parent_id,
      total_slots: !isArea ? totalSlots : 0,
      maximum_slots_per_user: !isArea ? maximumSlotsPerUser : 0,
      is_available: request.is_available,
      is_open: isOpen,
      // Optional — undefined is omitted from the JSON body, so callers
      // that don't manage dedication leave it untouched server-side.
      is_dedicated: request.is_dedicated,
      is_walkup: request.is_walkup,
      is_approvable: request.is_approvable,
      // Same contract as is_dedicated: undefined leaves the stored ticket types
      // alone, [] clears them. Only accepted when is_approvable is true.
      ticket_type_ids: request.ticket_type_ids,
      weight: request.weight,
      medias: [],
      // This prevents API to reset the medias when updating a resource
      medias_patch: true,
      map_data: request.map_data
    };
  }

  async resourceManagerPermissionsAll(userId: string, filter?: {}) {
    const params = new URLSearchParams();
    params.append("type", "area");
    params.append("type", "desk");
    params.append("type", "workspace");
    params.append("type", "meeting_room");
    return this.requestWithStatus<GetResourceManagerPermission>(
      "GET",
      `/users/${userId}/resources/managed?${params.toString()}`
    );
  }

  async resourceManagerPermissionsAdd(request: PostResourceManagerPermissions) {
    return await this.requestWithStatus("POST", `/resources/managers`, request);
  }

  async resourceManagerPermissionsDelete(
    res: IUserResourceManaged,
    userId: string
  ) {
    if (!res.manage) {
      throw new Error(`Delete can't be called without manage.`);
    }
    return await this.requestWithStatus(
      "DELETE",
      `/resources/${encodeURIComponent(res.id)}/managers/${encodeURIComponent(res.manage.permission_id)}/user/${userId}`
    );
  }

  async resourceDelete(id: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/v1/resources/${encodeURIComponent(id)}`
    );
  }

  async resourceScheduledPermissions(
    id: string,
    filter: ResourcePermissionFilter
  ) {
    return this.resourceScheduledPermissionsAll([id], filter);
  }

  async resourceScheduledPermissionsAll(
    ids: string[],
    filter?: ResourcePermissionFilter
  ) {
    const params = new URLSearchParams();
    ids.forEach((id) => params.append("resource", id));
    if (filter && filter.includePast) {
      params.append("include_past", "true");
    }
    return this.requestWithStatus<GetResourceSchedulePermission>(
      "GET",
      `/v1/resources/permissions?${params.toString()}`
    );
  }

  async resourceScheduledPermissionsAdd(
    request: PostResourceScheduledPermissions
  ) {
    return await this.requestWithStatus(
      "POST",
      `/v1/resources/permissions`,
      request
    );
  }

  async getResourceAdditionalAccess(resourceId: string) {
    return this.requestWithStatus<ResourceAdditionalAccessOptions[]>(
      "GET",
      `/v1/resources_additional_access?resource_id=${resourceId}`
    );
  }

  async setResourceAdditionalAccessOptions(
    resourceId: string,
    options: Partial<ResourceAdditionalAccessOptions>
  ) {
    const existingOptions = await this.getResourceAdditionalAccess(resourceId);
    const existingOption = (existingOptions.result || []).find(
      (option) => option.resource_id === resourceId
    );

    const additionalAccessData = {
      ...options,
      resource_id: resourceId
    } satisfies Partial<ResourceAdditionalAccessOptions>;

    return this.requestWithStatus(
      existingOption ? "PUT" : "POST",
      `/v1/resources_additional_access${existingOption ? `/${existingOption.id}` : ""}`,
      additionalAccessData
    );
  }

  async resourceScheduledPermissionsUpdate(
    request: PutResourceScheduledPermissions,
    resourceId: string,
    permissionId: number,
    teamId?: string | null,
    userId?: string | null
  ) {
    const permissionType: string = teamId ? `team/${teamId}` : `user/${userId}`;
    return await this.requestWithStatus(
      "PUT",
      `/v1/resources/${encodeURIComponent(resourceId)}/permissions/${encodeURIComponent(permissionId)}/${permissionType}`,
      request
    );
  }

  async resourceScheduledPermissionsDelete(permission: IResourcePermission) {
    let permissionType = "";

    if (permission.team) {
      permissionType = `team/${permission.team.id}`;
    } else if (permission.user) {
      permissionType = `user/${permission.user.id}`;
    }

    return await this.requestWithStatus(
      "DELETE",
      `/v1/resources/${encodeURIComponent(permission.resource_id)}/permissions/${encodeURIComponent(
        permission.id
      )}/${permissionType}`
    );
  }

  async newsAdd(body: object) {
    return await this.requestWithStatus("POST", "/news", body);
  }

  async news(office_id: string | null = null) {
    return await this.requestWithStatus<ResGetNews>(
      "GET",
      (office_id !== null
        ? `/offices/${encodeURIComponent(office_id)}/news`
        : "/news") +
        "?limit=" +
        BaseOrganizationEndpoints.MAXIMUM_LIMIT
    );
  }

  async getNews(id: string) {
    return await this.requestWithStatus(
      "GET",
      "/news/" + encodeURIComponent(id)
    );
  }

  async newsUpdate(id: string, body: object) {
    return await this.requestWithStatus(
      "PUT",
      `/news/${encodeURIComponent(id)}`,
      body
    );
  }

  async newsDelete(id: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/news/${encodeURIComponent(id)}`
    );
  }

  async questionAdd(body: object) {
    return await this.requestWithStatus("POST", "/questions", body);
  }

  async questions() {
    return await this.requestWithStatus<ResGetQuestions>(
      "GET",
      "/questions?limit=" + BaseOrganizationEndpoints.MAXIMUM_LIMIT
    );
  }

  async question(id: string) {
    return await this.requestWithStatus<ResGetQuestion>(
      "GET",
      `/questions/${encodeURIComponent(id)}`
    );
  }

  async questionUpdate(id: string, body: object) {
    return await this.requestWithStatus(
      "PUT",
      `/questions/${encodeURIComponent(id)}`,
      body
    );
  }

  async questionDelete(id: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/questions/${encodeURIComponent(id)}`
    );
  }

  /**
   * Returns health questionnaire questions for the specified office.
   *
   * @param officeId The office id to get the health questionnaire for
   * @param questionnaireId Optional parameter get details about a specific questionnaire
   */
  async questionnaire(
    officeId: string,
    questionnaireId?: string | null
  ): Promise<ResponseWithStatus<ResGetOfficesQuestionnaire>> {
    return await this.requestWithStatus<ResGetOfficesQuestionnaire>(
      "GET",
      questionnaireId
        ? `/questionnaires/${encodeURIComponent(questionnaireId)}`
        : `/offices/${encodeURIComponent(officeId)}/questionnaire`
    );
  }

  /**
   * Responds to a health questionnaires questions, triggered by a reservation in an office that requires one.
   *
   * @param officeId The office ID to which the reservation belongs
   * @param body The full body of the request
   */
  async questionnaireRespond(
    officeId: string,
    body: QuestionnaireRequest
  ): Promise<ResponseWithStatus<ResGetOfficesQuestionnaire>> {
    return await this.requestWithStatus(
      "PUT",
      `/offices/${encodeURIComponent(officeId)}/questionnaire`,
      body
    );
  }

  async generateOtp() {
    return await this.requestWithStatus<{ code: string; lifetime: number }>(
      "POST",
      "/codes"
    );
  }

  async uploadSignature(
    formId: string,
    signature: {
      user_signature: string;
      pdf_bytes: string;
      name: string;
      form_version: string | null;
      date_signed: string;
      consented: string | null;
      station_id: string;
      user_id: string;
      visit_id: string;
    }
  ) {
    return await this.requestWithStatus(
      "PUT",
      `/forms/${formId}/sign`,
      signature
    );
  }

  async notificationListen(
    listener: (type: string, data: object) => void
  ): Promise<signalR.HubConnection> {
    const accessToken = this.getAccessToken();
    const slug = this.getSlug();
    if (accessToken === null || slug === null) {
      throw new Error("Missing access token or slug");
    }

    const url = this.getBaseUrl();

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${url}/hub/notifications`, {
        accessTokenFactory: () => accessToken,
        headers: {
          "X-Tactic-Orgz": getHeaderSlug(slug)
        }
      })
      .build();

    connection.on("added", (type: string, json: string | null) => {
      const data = json !== null ? JSON.parse(json) : null;
      listener(type, data);
    });

    return connection;
  }

  private formatOffices(
    offices: ResGetOffices,
    user?: ResGetUser
  ): ResGetFormattedOffices {
    // Guard against an office ID being saved for a user that's not active
    const defaultOfficeNotDeleted = user
      ? offices.elements.some(
          (office) => office.id === user.organization_user?.default_office_id
        )
      : false;
    const officeId =
      user && defaultOfficeNotDeleted
        ? user.organization_user?.default_office_id
        : offices.elements[0].id;

    let office = null,
      byId: { [key: string]: CoreOffices } = {};
    for (const currentOffice of offices.elements) {
      // casting to CoreOffices to avoid type error, this is only available from '/v1/offices'
      byId[currentOffice.id] = currentOffice as CoreOffices;
      if (office == null && currentOffice.id == officeId) {
        office = currentOffice;
      }
    }
    const result: ResGetFormattedOffices = {
      officeId: officeId,
      office: office,
      offices: offices.elements,
      byId: byId
    };
    return result;
  }

  private normalizeEntityCollection(collection: any[]): { value: string }[] {
    return Array.isArray(collection)
      ? collection
      : Object.keys(collection).map((value) => ({ value }));
  }

  private getResourceEntity(type: string | null, body: string): object {
    const posted = JSON.parse(body);
    type = type !== null && type.indexOf(",") < 0 ? type : posted.type || null;
    if (!type) {
      throw new Error("No resource type");
    }

    const isArea = type == "area";
    const isApprovable =
      typeof posted.is_approvable !== "undefined" &&
      posted.is_approvable == true;
    const isOpen = typeof posted.is_open !== "undefined" && posted.is_open;
    const teams = posted.teams
      ? this.normalizeEntityCollection(posted.teams)
      : [];
    const users: { value: string }[] = posted.users
      ? this.normalizeEntityCollection(posted.users)
      : [];

    const usersSettings: { [key: string]: boolean } = {};
    const teamsSettings: { [key: string]: boolean } = {};

    for (const option of users) {
      usersSettings[option.value] = isApprovable;
    }
    for (const option of teams) {
      teamsSettings[option.value] = isApprovable;
    }

    const totalSlots =
      posted.total_slots && Number.parseInt(posted.total_slots, 10) > 0
        ? Number.parseInt(posted.total_slots, 10)
        : 1;
    const maximumSlotsPerUser =
      posted.maximum_slots_per_user &&
      Number.parseInt(posted.maximum_slots_per_user, 10) > 0
        ? Number.parseInt(posted.maximum_slots_per_user, 10)
        : 1;

    return {
      type: type,
      name: posted.name,
      map_url: posted.map_url,
      parent_id: posted.parent_id,
      total_slots: !isArea ? totalSlots : 0,
      maximum_slots_per_user: !isArea ? maximumSlotsPerUser : 0,
      is_available: posted.is_available,
      is_open: isOpen,
      is_approvable: isApprovable,
      teams: teamsSettings,
      users: usersSettings,
      weight: posted.weight
    };
  }

  private getJoinUrl(joinCode: string): string {
    return this.getUrl(`signup?join=${encodeURIComponent(joinCode)}`);
  }

  async getUploadUrl(options: UploadImageOptions, file: File) {
    const queryParams = new URLSearchParams({
      type: options.type,
      format: options.format
    });

    return this.requestWithStatus<{ url?: string; upload_url?: string }>(
      "POST",
      `/v1/uploads/images/url?${queryParams.toString()}`,
      file
    );
  }

  async getGanttOverviewEntities(
    officeId: string,
    filters: GetGantOverviewFilters
  ) {
    const params = new URLSearchParams();
    params.set("start", filters.start.toISOString());
    params.set("end", filters.end.toISOString());
    if (filters.limit !== undefined) {
      params.set("limit", filters.limit.toString());
    }
    if (filters.cursor) params.set("cursor", filters.cursor);
    if (filters.floorId) params.set("floor_id", filters.floorId);
    if (filters.resourceType) params.set("resource_type", filters.resourceType);
    if (filters.resourceIds) {
      for (const id of filters.resourceIds) {
        params.append("resource_id", id);
      }
    }

    return this.requestWithStatus<GanttOverviewResponse>(
      "GET",
      `/v1/dashboard/offices/${officeId}/gantt_overview?${params.toString()}`
    );
  }

  // Get the availability of the offices for the given dates
  async getOfficesAvailability(officeIds: string[], dates: string[]) {
    const params: [string, string][] = [];
    for (const officeId of officeIds) {
      params.push(["office_id", officeId]);
    }
    for (const date of dates) {
      params.push(["date", date]);
    }
    const queryString = new URLSearchParams(params).toString();

    return this.requestWithStatus<OfficesAvailabilityResponse>(
      "GET",
      `/v1/dashboard/offices/availability?${queryString}`
    );
  }
}
